import React, { useState } from 'react';
import { doc, updateDoc, addDoc, collection, serverTimestamp, Timestamp, deleteField } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { uploadPhotos, uploadDataUrl } from '../../firebase/storageService';
import SignatureCanvas from './SignatureCanvas';
import { useAuth } from '../../contexts/AuthContext';
import { COLORS } from '../../styles/tokens';
import { deliveryStatusFor, isCarrierOrder } from '../../utils/carrierOrder';

export default function DeliveryStep({ order }) {
  const { currentUser } = useAuth();
  const [deliveryPhotos, setDeliveryPhotos] = useState([]);
  const [previews,       setPreviews]       = useState([]);
  const [signatureUrl,   setSignatureUrl]   = useState(null); // dataURL fra canvas
  const [signatureSaved, setSignatureSaved] = useState(false);
  const [loading,        setLoading]        = useState(false);
  const [error,          setError]          = useState('');

  function handlePhotos(e) {
    const files = Array.from(e.target.files).slice(0, 5);
    setDeliveryPhotos(files);
    setPreviews(files.map(f => URL.createObjectURL(f)));
  }

  function handleSignatureSave(dataUrl) {
    setSignatureUrl(dataUrl);
    setSignatureSaved(!!dataUrl);
  }

  function validate() {
    if (!signatureSaved || !signatureUrl) return 'Innhent signatur fra mottaker';
    return null;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const err = validate();
    if (err) { setError(err); return; }

    setLoading(true);
    setError('');

    try {
      // Last opp bilder og signatur parallelt
      const [deliveryUrls, receiverSigUrl] = await Promise.all([
        deliveryPhotos.length > 0 ? uploadPhotos(deliveryPhotos, `deliveries/${order.id}/delivery`) : Promise.resolve([]),
        uploadDataUrl(signatureUrl, `deliveries/${order.id}/signature/${Date.now()}.png`),
      ]);

      const pendingAdj = order.pendingAdjustment;

      // Opprett delivery-dokument
      await addDoc(collection(db, 'deliveries'), {
        orderId:                   order.id,
        driverUid:                 currentUser.uid,
        pickupPhotoUrls:           order.pickupPhotoUrls ?? [],
        deliveryPhotoUrls:         deliveryUrls,
        actualTons:                order.pickupActualTons ?? order.estimatedTons ?? null,
        weightDocumentUrl:         order.weightDocumentUrl ?? '',
        weightDeviation:           pendingAdj?.deviationTons ?? 0,
        priceAdjustment:           pendingAdj?.priceAdjustment ?? 0,
        adjustmentApprovedByOrderer: pendingAdj?.approvedByOrderer ?? true,
        receiverSignatureUrl:      receiverSigUrl,
        completedAt:               serverTimestamp(),
      });

      // Markedsplass: delivered_pending venter på bestillers bekreftelse (24 timer).
      // Egne ordre har ingen bestiller, så sjåføren fullfører direkte.
      const confirmDeadline = new Date();
      confirmDeadline.setHours(confirmDeadline.getHours() + 24);
      await updateDoc(doc(db, 'orders', order.id), {
        status:                  deliveryStatusFor(order),
        deliveryPhotoUrls:       deliveryUrls,
        receiverSignatureUrl:    receiverSigUrl,
        deliveredAt:             serverTimestamp(),
        ...(isCarrierOrder(order) ? {} : { deliveryConfirmDeadline: Timestamp.fromDate(confirmDeadline) }),
        // Posisjonsdeling slutter ved levering (jf. samtykketeksten)
        driverLocation:          deleteField(),
      });
    } catch (err) {
      console.error(err);
      setError('Opplasting feilet. Sjekk internettforbindelsen og prøv igjen.');
    } finally {
      setLoading(false);
    }
  }

  // Sjekk om vektavvik venter på godkjenning fra bestiller
  const awaitingApproval = order.pendingAdjustment && !order.pendingAdjustment.approvedByOrderer;

  return (
    <form onSubmit={handleSubmit} style={styles.form}>

      {awaitingApproval && (
        <div style={styles.waitingBox}>
          ⏳ Venter på at bestiller godkjenner vektavviket på{' '}
          <strong>{order.pendingAdjustment.deviationPct}%</strong>
          {' '}(kr {order.pendingAdjustment.priceAdjustment > 0 ? '+' : ''}{order.pendingAdjustment.priceAdjustment}
          ,- prisjustering)
          <br />
          <span style={{ fontSize: 12, opacity: 0.8, marginTop: 4, display: 'block' }}>
            Du kan registrere levering mens du venter
          </span>
        </div>
      )}

      <SectionTitle>1. Foto av levert last</SectionTitle>
      <p style={styles.hint}>Valgfritt, maks. 5 bilder</p>

      <label style={styles.fileLabel}>
        📷 Velg bilder
        <input
          type="file"
          accept="image/*"
          multiple
          capture="environment"
          onChange={handlePhotos}
          style={{ display: 'none' }}
        />
      </label>

      {previews.length > 0 && (
        <div style={styles.previews}>
          {previews.map((src, i) => (
            <img key={i} src={src} alt={`Bilde ${i + 1}`} style={styles.preview} />
          ))}
        </div>
      )}

      <SectionTitle>2. Mottakers signatur</SectionTitle>
      <p style={styles.hint}>La mottaker signere direkte på skjermen</p>
      <SignatureCanvas onSave={handleSignatureSave} />
      {signatureSaved && (
        <p style={styles.fileConfirm}>✓ Signatur registrert</p>
      )}

      {error && <p style={styles.error}>{error}</p>}

      <button type="submit" style={styles.btn} disabled={loading}>
        {loading ? 'Fullfører…' : '✅ Fullfør levering'}
      </button>
    </form>
  );
}

function SectionTitle({ children }) {
  return <h3 style={{ fontSize: 15, fontWeight: 700, color: COLORS.textDark, margin: '16px 0 6px' }}>{children}</h3>;
}

const styles = {
  form:       { display: 'flex', flexDirection: 'column', gap: 8 },
  hint:       { fontSize: 13, color: COLORS.textLight, margin: 0 },
  waitingBox: { background: COLORS.warningBg, border: `1px solid ${COLORS.warningBorder}`, borderRadius: 8, padding: '12px 16px', fontSize: 13, color: COLORS.warningText },
  fileLabel:  { display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderRadius: 8, border: `2px dashed ${COLORS.border}`, cursor: 'pointer', fontSize: 14, color: COLORS.textMid, background: COLORS.surfaceHover, width: 'fit-content' },
  previews:   { display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 },
  preview:    { width: 80, height: 80, objectFit: 'cover', borderRadius: 6 },
  fileConfirm:{ fontSize: 13, color: COLORS.success, margin: '4px 0 0' },
  error:      { background: COLORS.dangerBg, color: COLORS.dangerText, padding: '10px 12px', borderRadius: 6, fontSize: 14 },
  btn:        { height: 54, borderRadius: 27, border: 'none', background: COLORS.primary, color: COLORS.onPrimary, fontSize: 16, fontWeight: 700, cursor: 'pointer', marginTop: 12 },
};
